import { z } from 'zod';
import { Classification } from './classification.js';
import { SourceRef } from './source-ref.js';
import { StructuredDocument } from './document.js';

/**
 * Knowledge model (04-KNOWLEDGE-MEMORY, ADR-013/014/029). Four kinds, orthogonal attributes,
 * temporal validity and provenance. "The AI suggested this" (CANDIDATE/INFERRED) is always
 * distinguishable from "the organization knows this" (VALIDATED).
 */
export const KNOWLEDGE_KINDS = ['ENTITY', 'ASSERTION', 'GUIDANCE', 'ARTIFACT'] as const;
export const KnowledgeKind = z.enum(KNOWLEDGE_KINDS);
export type KnowledgeKind = z.infer<typeof KnowledgeKind>;

export const KNOWLEDGE_STATUSES = [
  'DISCOVERED',
  'CANDIDATE',
  'VALIDATED',
  'REJECTED',
  'SUPERSEDED',
  'DEPRECATED',
] as const;
export const KnowledgeStatus = z.enum(KNOWLEDGE_STATUSES);
export type KnowledgeStatus = z.infer<typeof KnowledgeStatus>;

export const EPISTEMIC_STATES = ['RETRIEVED', 'EXTRACTED', 'INFERRED', 'HUMAN_ASSERTED'] as const;
export const EpistemicState = z.enum(EPISTEMIC_STATES);
export type EpistemicState = z.infer<typeof EpistemicState>;

/** Generic Core types (domain packs add namespaced ones such as "ea.AAD"). */
export const CORE_KNOWLEDGE_TYPES: readonly {
  key: string;
  kind: KnowledgeKind;
  description: string;
}[] = [
  { key: 'System', kind: 'ENTITY', description: 'A system in the landscape.' },
  { key: 'Application', kind: 'ENTITY', description: 'An application.' },
  { key: 'API', kind: 'ENTITY', description: 'An API exposed by a system.' },
  { key: 'Interface', kind: 'ENTITY', description: 'An interface between systems.' },
  { key: 'Integration', kind: 'ENTITY', description: 'An integration connecting systems.' },
  { key: 'DataObject', kind: 'ENTITY', description: 'A business data object.' },
  { key: 'OrgUnit', kind: 'ENTITY', description: 'An organizational unit.' },
  { key: 'Fact', kind: 'ASSERTION', description: 'A factual statement.' },
  { key: 'Constraint', kind: 'ASSERTION', description: 'A constraint.' },
  { key: 'Dependency', kind: 'ASSERTION', description: 'A dependency between things.' },
  { key: 'Requirement', kind: 'ASSERTION', description: 'A requirement.' },
  { key: 'Decision', kind: 'ASSERTION', description: 'A decision.' },
  { key: 'LessonLearned', kind: 'ASSERTION', description: 'A lesson learned.' },
  { key: 'Policy', kind: 'GUIDANCE', description: 'A policy.' },
  { key: 'Standard', kind: 'GUIDANCE', description: 'A standard.' },
  { key: 'BestPractice', kind: 'GUIDANCE', description: 'A best practice.' },
  { key: 'Pattern', kind: 'GUIDANCE', description: 'A pattern.' },
  { key: 'AntiPattern', kind: 'GUIDANCE', description: 'An anti-pattern.' },
  { key: 'SecurityRequirement', kind: 'GUIDANCE', description: 'A security requirement.' },
  { key: 'Document', kind: 'ARTIFACT', description: 'A document.' },
  { key: 'SourcePage', kind: 'ARTIFACT', description: 'A page from a document source.' },
  { key: 'Ticket', kind: 'ARTIFACT', description: 'A ticket.' },
];

/**
 * Authority profile: ordered authority classes (higher first). Configuration per domain;
 * the EA default below is the PROPOSAL from 04 §3, to be confirmed by the domain owner.
 */
export const DEFAULT_AUTHORITY_PROFILE: readonly string[] = [
  'OFFICIAL_POLICY',
  'OFFICIAL_STANDARD',
  'APPROVED_DECISION',
  'APPROVED_ARTIFACT',
  'CURRENT_DOC',
  'HISTORICAL',
  'TICKET_CONTEXT',
  'LLM_INFERENCE',
];
export const AuthorityClass = z.string().regex(/^[A-Z][A-Z0-9_]{1,40}$/);

// ── Document capabilities (vendor-neutral) ──────────────────────────────────
/** A bare identifier (no spaces or labels), so a typo is refused when saved, not at sync. */
const RootId = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9._~-]+$/, 'Use the bare key or id, without labels or spaces');
export const PageRoots = z.object({
  spaces: z.array(RootId).max(50).default([]),
  pageIds: z.array(RootId).max(200).default([]),
});
export type PageRoots = z.infer<typeof PageRoots>;

export const PageChangesInput = z.object({
  roots: PageRoots,
  includeChildren: z.boolean().default(true),
  /** Opaque cursor from the previous call; null = full listing. */
  since: z.string().max(200).nullable().default(null),
  limit: z.number().int().min(1).max(500).default(100),
});
export const PageChange = z.object({
  id: z.string(),
  version: z.string(),
  title: z.string(),
  updatedAt: z.iso.datetime().nullable(),
  deleted: z.boolean().default(false),
});
export const PageChangesOutput = z.object({
  changes: z.array(PageChange),
  cursor: z.string().nullable(),
  hasMore: z.boolean(),
});
export type PageChangesOutput = z.infer<typeof PageChangesOutput>;

export const PageReadInput = z.object({
  id: z.string().min(1).max(100),
  version: z.string().max(40).optional(),
  /** Also return the page as a StructuredDocument (ADR-030). */
  structure: z.boolean().default(false),
});
export const NormalizedPage = z.object({
  id: z.string(),
  title: z.string(),
  version: z.string(),
  space: z.string().nullable(),
  ancestors: z.array(z.object({ id: z.string(), title: z.string() })),
  /** Plain text with markdown-style headings ("# ", "## ") preserved for structure. */
  body: z.string(),
  labels: z.array(z.string()),
  author: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  /** Page-level read restrictions in the source system (ACL-aware retrieval). */
  restricted: z.boolean(),
  synthetic: z.boolean(),
  source: SourceRef,
  /** Present when requested with `structure: true` and supported by the adapter. */
  structure: StructuredDocument.optional(),
});
export type NormalizedPage = z.infer<typeof NormalizedPage>;

/** Attachments of a page (ADR-032; read-only). */
export const PageAttachmentsInput = z.object({ pageId: z.string().min(1).max(100) });
export const PageAttachmentsOutput = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      mediaType: z.string(),
      size: z.number().int().nullable(),
      version: z.string(),
    }),
  ),
});
export type PageAttachmentsOutput = z.infer<typeof PageAttachmentsOutput>;

export const AttachmentReadInput = z.object({
  pageId: z.string().min(1).max(100),
  attachmentId: z.string().min(1).max(100),
  /** Upper bound of returned text (the rest is reported as truncated). */
  maxChars: z.number().int().min(1000).max(400_000).default(200_000),
});
export const AttachmentText = z.object({
  id: z.string(),
  title: z.string(),
  mediaType: z.string(),
  version: z.string(),
  format: z.enum(['pdf', 'docx', 'text']),
  pages: z.number().int().nullable(),
  text: z.string(),
  truncated: z.boolean(),
  synthetic: z.boolean(),
  source: SourceRef,
});
export type AttachmentText = z.infer<typeof AttachmentText>;

export const PageSearchInput = z.object({
  query: z.string().min(1).max(500),
  spaces: z.array(z.string()).max(50).default([]),
  limit: z.number().int().min(1).max(50).default(10),
});
export const PageSearchOutput = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      version: z.string(),
      excerpt: z.string().nullable(),
    }),
  ),
});

// ── Knowledge sources ───────────────────────────────────────────────────────
export const KnowledgeSourceUpsert = z.object({
  name: z.string().regex(/^[a-z0-9][a-z0-9._-]{1,62}$/),
  description: z.string().max(500).default(''),
  connectionId: z.string().uuid(),
  roots: PageRoots,
  includeChildren: z.boolean().default(true),
  authorityClass: AuthorityClass,
  classification: Classification,
  scheduleMinutes: z.number().int().min(15).max(10_080).nullable().default(null),
  status: z.enum(['active', 'paused']).default('active'),
});
export type KnowledgeSourceUpsert = z.infer<typeof KnowledgeSourceUpsert>;

export const KnowledgeSourceView = KnowledgeSourceUpsert.extend({
  id: z.string().uuid(),
  connectionName: z.string(),
  lastSyncAt: z.iso.datetime().nullable(),
  documents: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type KnowledgeSourceView = z.infer<typeof KnowledgeSourceView>;

export const IngestionRunView = z.object({
  id: z.string().uuid(),
  sourceId: z.string().uuid(),
  trigger: z.enum(['manual', 'schedule', 'research']),
  status: z.enum(['queued', 'running', 'succeeded', 'partial', 'failed']),
  counts: z.record(z.string(), z.number()),
  documents: z.array(
    z.object({
      externalId: z.string(),
      version: z.string(),
      action: z.enum(['ingested', 'skipped', 'deleted', 'failed']),
      error: z.string().optional(),
    }),
  ),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  startedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
});
export type IngestionRunView = z.infer<typeof IngestionRunView>;

// ── Search ──────────────────────────────────────────────────────────────────
export const SearchKnowledgeInput = z.object({
  query: z.string().min(2).max(500).describe('What to look for (natural language or identifiers).'),
  types: z
    .array(z.string())
    .max(20)
    .optional()
    .describe('Restrict to knowledge types, e.g. ["Standard"].'),
  include: z.array(z.enum(['items', 'documents'])).default(['items', 'documents']),
  statuses: z
    .array(KnowledgeStatus)
    .max(6)
    .optional()
    .describe('Default: VALIDATED and CANDIDATE.'),
  asOf: z.iso
    .datetime()
    .optional()
    .describe('Return what was valid at this instant (default: now).'),
  expandGraph: z.boolean().default(false),
  limit: z.number().int().min(1).max(50).default(10),
  /** Restrict document results to these source spaces (knowledge items are not space-bound). */
  spaces: z.array(z.string().min(1).max(100)).max(20).optional(),
  /** Only results of these authority classes (e.g. official standards, not examples). */
  authorityClasses: z.array(AuthorityClass).max(10).optional(),
  /** Length of each hit's snippet; context packs ask for whole passages. */
  snippetChars: z.number().int().min(100).max(4000).default(400),
});
export type SearchKnowledgeInput = z.infer<typeof SearchKnowledgeInput>;

export const SearchHit = z.object({
  kind: z.enum(['item', 'chunk']),
  id: z.string().uuid(),
  title: z.string(),
  snippet: z.string(),
  score: z.number(),
  matchedBy: z.array(z.enum(['vector', 'fulltext', 'trigram', 'graph'])),
  itemType: z.string().nullable(),
  status: KnowledgeStatus.nullable(),
  authorityClass: z.string(),
  classification: Classification,
  validFrom: z.iso.datetime().nullable(),
  validTo: z.iso.datetime().nullable(),
  source: SourceRef.nullable(),
});
export type SearchHit = z.infer<typeof SearchHit>;

export const SearchKnowledgeResult = z.object({
  hits: z.array(SearchHit),
  retrieval: z.object({
    vector: z.enum(['enabled', 'disabled', 'refused']),
    embeddingModel: z.string().nullable(),
    asOf: z.iso.datetime(),
  }),
});
export type SearchKnowledgeResult = z.infer<typeof SearchKnowledgeResult>;

// ── Items ───────────────────────────────────────────────────────────────────
export const ProvenanceView = z.object({
  id: z.string().uuid(),
  method: z.enum(['RETRIEVED', 'EXTRACTED', 'INFERRED', 'HUMAN']),
  documentId: z.string().uuid().nullable(),
  documentTitle: z.string().nullable(),
  documentVersion: z.string().nullable(),
  quote: z.string().nullable(),
  quoteHash: z.string().nullable(),
  url: z.string().nullable(),
  executionId: z.string().uuid().nullable(),
  retrievedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export const KnowledgeItemView = z.object({
  id: z.string().uuid(),
  type: z.string(),
  kind: KnowledgeKind,
  title: z.string(),
  summary: z.string(),
  body: z.record(z.string(), z.unknown()),
  status: KnowledgeStatus,
  epistemic: EpistemicState,
  authorityClass: z.string(),
  classification: Classification,
  confidence: z.number(),
  validFrom: z.iso.datetime().nullable(),
  validTo: z.iso.datetime().nullable(),
  recordedAt: z.iso.datetime(),
  supersededAt: z.iso.datetime().nullable(),
  supersededBy: z.string().uuid().nullable(),
  validatedBy: z.string().uuid().nullable(),
  validatedAt: z.iso.datetime().nullable(),
  reviewNote: z.string().nullable(),
  provenance: z.array(ProvenanceView),
  edges: z.array(
    z.object({
      id: z.string().uuid(),
      direction: z.enum(['out', 'in']),
      predicate: z.string(),
      otherId: z.string().uuid(),
      otherTitle: z.string(),
      status: z.string(),
    }),
  ),
});
export type KnowledgeItemView = z.infer<typeof KnowledgeItemView>;

export const KnowledgeReview = z.object({
  decision: z.enum(['validate', 'reject', 'deprecate']),
  note: z.string().max(1000).optional(),
  validFrom: z.iso.datetime().optional(),
  validTo: z.iso.datetime().optional(),
});

export const KnowledgeSupersede = z.object({
  supersededById: z.string().uuid(),
  note: z.string().max(1000).optional(),
});

/** Explanation of why the platform believes something (evidence chain). */
export const Explanation = z.object({
  subject: z.object({
    kind: z.enum(['item', 'execution_field']),
    id: z.string(),
    title: z.string(),
  }),
  status: z.string(),
  epistemic: z.string().nullable(),
  statement: z.string(),
  evidence: z.array(
    z.object({
      method: z.string(),
      source: SourceRef.nullable(),
      quote: z.string().nullable(),
      validatedBy: z.string().nullable(),
      validatedAt: z.iso.datetime().nullable(),
    }),
  ),
  history: z.array(z.object({ at: z.iso.datetime(), event: z.string() })),
});
export type Explanation = z.infer<typeof Explanation>;

// ── Context packs ───────────────────────────────────────────────────────────
export const KnowledgeRequirement = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,62}$/),
  description: z.string().min(1).max(300),
  /** Search query; may contain ${...} references resolved by the step. */
  query: z.string().min(1).max(500),
  types: z.array(z.string()).max(20).optional(),
  /** Minimum relevant results to consider the requirement SATISFIED. */
  minResults: z.number().int().min(1).max(20).default(1),
  /** Where targeted research looks for this requirement (document spaces); optional. */
  spaces: z.array(z.string().min(1).max(100)).max(20).optional(),
  /**
   * Only knowledge of these authority classes satisfies the requirement (e.g. a standard must
   * come from an official source, not from an example that mentions it). Research results are
   * ingested into a source of the connection with one of these classes when there is one.
   */
  authorityClasses: z.array(AuthorityClass).max(10).optional(),
  /** What recall looks at (default: documents when `spaces` is set, else items and documents). */
  include: z
    .array(z.enum(['items', 'documents']))
    .min(1)
    .optional(),
  /** Knowledge statuses that count (default: validated and candidate). */
  statuses: z.array(KnowledgeStatus).max(6).optional(),
  /** false = recall only: a gap never triggers live research (e.g. reviewed knowledge). */
  research: z.boolean().optional(),
});
export type KnowledgeRequirement = z.infer<typeof KnowledgeRequirement>;
